import React, { useState, useEffect } from "react";
import { faTimes, faPrint } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import ErrorModal from "./ErrorModal";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  user: User | null;
  qualifications: Qualification[];
  techFolio: TechFolio | null;
}

type User = {
  id: number;
  last_name: string;
  first_name: string;
  last_name_en: string;
  first_name_en: string;
  name_ini: string;
  name: string;
  name_en: string;
  nationality: string;
  gender: string;
  age: number;
  birth_date: string;
  current_address: string;
  sheets: {
    updated_at: string;
  };
  isSelected: boolean;
  isTarget: boolean;
};

type Qualification = {
  id: string | "";
  qualification_id: string | null;
  name: string | null;
  get_date: string | null;
  isDisplay: boolean;
};

type SheetOption = {
  logo: string;
  name: string;
  nationality: string;
  gender: string;
  age: string;
  birthday: string;
  address: string;
  isDisplayedLogo: boolean;
  isDisplayedName: boolean;
  isDisplayedNameIni: boolean;
  isDisplayedNationality: boolean;
  isDisplayedGender: boolean;
  isDisplayedAge: boolean;
  isDisplayedBirthday: boolean;
  isDisplayedAddress: boolean;
};

type TechFolio = {
  id: string | null;
  specialty: string;
  tech_experiences?: {
    id: string | null;
    skill_type: number;
    sequence_num: number;
    industry: string;
    system_name: string;
    system_detail: string;
    assigned_task: string;
    work_contents?: {
      id: number;
      name: string;
    }[];
    position_id: number;
    position?: {
      id: string | null;
      name: string;
    };
    team: string;
    member_count: string;
    env1: string;
    env2: string;
    env3: string;
    env4: string;
    env5: string;
    work_start_period: string;
    work_end_period: string;
    period_year: number | null;
    period_month: number | null;
    client: string;
    end_user: string;
    remarks: string;
  }[];
};

const MAX_LENGTHS = {
  localQualifications: 7,
};

const PrintPreviewModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  user,
  qualifications,
  techFolio,
}) => {
  // モーダル内でのみ使用するisDisplay付きの資格リストを管理
  const [localQualifications, setLocalQualifications] = useState<
    Qualification[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // モーダルが開くたびに初期化
      const total = qualifications.length;
      const initializedQualifications = qualifications.map((q, index) => ({
        ...q,
        isDisplay: index >= total - 7,
      }));
      setLocalQualifications(initializedQualifications);
    }
  }, [isOpen, qualifications]);

  const [sheetOption, setSheetOption] = useState<SheetOption>({
    logo: "表示",
    name: "表示",
    nationality: "表示",
    gender: "表示",
    age: "非表示",
    birthday: "非表示",
    address: "表示",
    isDisplayedLogo: true,
    isDisplayedName: true,
    isDisplayedNationality: true,
    isDisplayedNameIni: false,
    isDisplayedGender: true,
    isDisplayedAge: false,
    isDisplayedBirthday: false,
    isDisplayedAddress: true,
  });
  const logo_options = ["表示", "非表示"];
  const name_options = ["表示", "イニシャル", "非表示"];
  const nationality_options = ["表示", "非表示"];
  const gender_options = ["表示", "非表示"];
  const age_options = ["表示", "非表示"];
  const birthday_options = ["表示", "非表示"];
  const address_options = ["表示", "非表示"];
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "PDFの生成に失敗しました。再試行してください。"
  );

  // プロフィールオプションの変更処理
  const handleOptionChange = (setName: string, value: string) => {
    if (setName === "logo") {
      setSheetOption((prevState) => ({
        ...prevState,
        [setName]: value,
        isDisplayedLogo: !sheetOption.isDisplayedLogo,
      }));
    } else if (setName === "name") {
      if (value === "表示") {
        setSheetOption((prevState) => ({
          ...prevState,
          [setName]: value,
          isDisplayedName: true,
          isDisplayedNameIni: false,
        }));
      } else if (value === "イニシャル") {
        setSheetOption((prevState) => ({
          ...prevState,
          [setName]: value,
          isDisplayedName: false,
          isDisplayedNameIni: true,
        }));
      } else {
        setSheetOption((prevState) => ({
          ...prevState,
          [setName]: value,
          isDisplayedName: false,
          isDisplayedNameIni: false,
        }));
      }
    } else if (setName === "nationality") {
      setSheetOption((prevState) => ({
        ...prevState,
        [setName]: value,
        isDisplayedNationality: !sheetOption.isDisplayedNationality,
      }));
    } else if (setName === "gender") {
      setSheetOption((prevState) => ({
        ...prevState,
        [setName]: value,
        isDisplayedGender: !sheetOption.isDisplayedGender,
      }));
    } else if (setName === "age") {
      setSheetOption((prevState) => ({
        ...prevState,
        [setName]: value,
        isDisplayedAge: !sheetOption.isDisplayedAge,
      }));
    } else if (setName === "birthday") {
      setSheetOption((prevState) => ({
        ...prevState,
        [setName]: value,
        isDisplayedBirthday: !sheetOption.isDisplayedBirthday,
      }));
    } else if (setName === "address") {
      setSheetOption((prevState) => ({
        ...prevState,
        [setName]: value,
        isDisplayedAddress: !sheetOption.isDisplayedAddress,
      }));
    }
  };

  // 資格オプションでのラジオボタン修正
  const handleQCOptionChange = (id: string, isDisplay: boolean) => {
    setLocalQualifications((prev) =>
      prev.map((qualification) =>
        qualification.id === id
          ? { ...qualification, isDisplay }
          : qualification
      )
    );
  };

  // 印刷プレビュー表示
  const createPdf = async () => {
    // 表示できる数が最大数を超えていたらアラートを出して処理を中断
    const isDisplayCount = localQualifications.filter(
      (q) => q.isDisplay ?? true
    ).length;
    if (isDisplayCount > MAX_LENGTHS.localQualifications) {
      alert(
        `表示できる項目は最大 ${MAX_LENGTHS.localQualifications} 件までです。`
      );
      return;
    }

    try {
      setIsLoading(true);
      if (techFolio === null) {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/tf/my-sheet/pdf`,
          {
            params: {
              userId: user?.id,
              sheetOption: sheetOption,
              qualifications: localQualifications,
              techFolio: techFolio,
            },
            responseType: "blob", // PDFファイルをblobとして取得
            withCredentials: true, // クッキーを自動的に送信
          }
        );
        // BlobデータをURLに変換して表示
        const fileURL = URL.createObjectURL(response.data as Blob);
        window.open(fileURL, "_blank"); // 新しいウィンドウでPDFを表示
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/tf/my-sheet/pdf`,
          {
            userId: user?.id,
            sheetOption: sheetOption,
            qualifications: localQualifications,
            techFolio: techFolio,
          },
          {
            withCredentials: true, // クッキーを自動的に送信
            responseType: "blob", // PDFファイルをblobとして取得
          }
        );
        // BlobデータをURLに変換して表示
        const fileURL = URL.createObjectURL(response.data as Blob);
        window.open(fileURL, "_blank"); // 新しいウィンドウでPDFを表示
      }
    } catch (error) {
      console.error("PDFの生成に失敗しました:", error);
      setErrorMessage("PDFの生成に失敗しました。再試行してください。");
      setErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-start justify-center pt-10 z-50">
      <div className="bg-white rounded-2xl shadow-lg w-2/3 md:w-1/2 lg:w-3/5 p-6 relative">
        <div className="w-11/12 mx-auto">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            &times;
          </button>
          <div className="flex mb-4">
            <div className="border-l-8 rounded-3xl border-slate-100 mr-4"></div>
            {title && (
              <h2 className="text-lg text-slate-500 font-black">{title}</h2>
            )}
          </div>
          <div className="mb-6">
            <div>
              <div className="flex mb-2">
                <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                <div className="text-lg text-slate-500 font-black">
                  プロフィール
                </div>
              </div>
              <div className=" border-2 border-slate-300 rounded-2xl overflow-hidden mb-6">
                {isLoading ? (
                  <table className="table-auto text-slate-600 w-full border-collapse">
                    <tbody>
                      <tr>
                        <th className="h-10 w-28 bg-slate-100 px-4 py-2 text-right border-r-2">
                          社名
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="h-9 bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          氏名
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="h-9 bg-slate-100 px-4 py-2 text-right border-r-2">
                          国籍
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="h-9 bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          性別
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="h-9 bg-slate-100 px-4 py-2 text-right border-r-2">
                          年齢
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="h-9 bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          生年月日
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                      <tr>
                        <th className="h-9 bg-slate-100 px-4 py-2 text-right border-r-2">
                          現住所
                        </th>
                        <td className="bg-slate-50">
                          <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <table className="table-auto text-slate-600 w-full border-collapse">
                    <tbody>
                      <tr>
                        <th className="w-28 bg-slate-100 px-4 py-2 text-right border-r-2">
                          社名
                        </th>
                        <td className="bg-white  pl-4 py-2 ">
                          {sheetOption.isDisplayedLogo && "iSopra"}
                        </td>
                        <td className="bg-white  px-4 py-2">
                          {logo_options.map((option) => (
                            <label key={option} className="pl-4">
                              <input
                                type="radio"
                                name="logo"
                                value={option}
                                checked={sheetOption.logo === option}
                                onChange={(e) =>
                                  handleOptionChange("logo", e.target.value)
                                }
                              />
                              {option}
                            </label>
                          ))}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          氏名
                        </th>
                        <td className="bg-slate-50  pl-4 py-2 w-80">
                          {user && sheetOption.isDisplayedName ? (
                            <>
                              {user.last_name} {user.first_name}
                              <br />
                              {user.last_name_en} {user.first_name_en}
                            </>
                          ) : user ? (
                            ""
                          ) : (
                            "データなし"
                          )}
                          {user && sheetOption.isDisplayedNameIni
                            ? `${user.name_ini}`
                            : user
                            ? ""
                            : "データなし"}
                        </td>
                        <td className="bg-slate-50  px-4 py-2 whitespace-nowrap">
                          <div className="flex flex-row">
                            {name_options.map((option) => (
                              <label key={option} className="pl-4">
                                <input
                                  type="radio"
                                  name="name"
                                  value={option}
                                  checked={sheetOption.name === option}
                                  onChange={(e) =>
                                    handleOptionChange("name", e.target.value)
                                  }
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          国籍
                        </th>
                        <td className="bg-white  pl-4 py-2">
                          {user && sheetOption.isDisplayedNationality
                            ? user.nationality
                            : user
                            ? ""
                            : "データなし"}
                        </td>
                        <td className="bg-white  px-4 py-2">
                          {nationality_options.map((option) => (
                            <label key={option} className="pl-4">
                              <input
                                type="radio"
                                name="nationality"
                                value={option}
                                checked={sheetOption.nationality === option}
                                onChange={(e) =>
                                  handleOptionChange(
                                    "nationality",
                                    e.target.value
                                  )
                                }
                              />
                              {option}
                            </label>
                          ))}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          性別
                        </th>
                        <td className="bg-slate-50  pl-4 py-2">
                          {user && sheetOption.isDisplayedGender
                            ? user.gender
                            : user
                            ? ""
                            : "データなし"}
                        </td>
                        <td className="bg-slate-50  px-4 py-2">
                          {gender_options.map((option) => (
                            <label key={option} className="pl-4">
                              <input
                                type="radio"
                                name="gender"
                                value={option}
                                checked={sheetOption.gender === option}
                                onChange={(e) =>
                                  handleOptionChange("gender", e.target.value)
                                }
                              />
                              {option}
                            </label>
                          ))}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          年齢
                        </th>
                        <td className="bg-white  pl-4 py-2">
                          {user && sheetOption.isDisplayedAge
                            ? user.age
                            : user
                            ? ""
                            : "データなし"}
                        </td>
                        <td className="bg-white  px-4 py-2">
                          {age_options.map((option) => (
                            <label key={option} className="pl-4">
                              <input
                                type="radio"
                                name="age"
                                value={option}
                                checked={sheetOption.age === option}
                                onChange={(e) =>
                                  handleOptionChange("age", e.target.value)
                                }
                              />
                              {option}
                            </label>
                          ))}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-eaf0f6 px-4 py-2 text-right border-r-2">
                          生年月日
                        </th>
                        <td className="bg-slate-50  px-4 py-2">
                          {user && sheetOption.isDisplayedBirthday
                            ? user.birth_date
                            : user
                            ? ""
                            : "データなし"}
                        </td>
                        <td className="bg-slate-50  px-4 py-2">
                          {birthday_options.map((option) => (
                            <label key={option} className="pl-4">
                              <input
                                type="radio"
                                name="birthday"
                                value={option}
                                checked={sheetOption.birthday === option}
                                onChange={(e) =>
                                  handleOptionChange("birthday", e.target.value)
                                }
                              />
                              {option}
                            </label>
                          ))}
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-slate-100 px-4 py-2 text-right border-r-2">
                          現住所
                        </th>
                        <td className="bg-white pl-4 py-2">
                          {user && sheetOption.isDisplayedAddress
                            ? user.current_address
                            : user
                            ? ""
                            : "データなし"}
                        </td>
                        <td className="bg-white px-4 py-2">
                          {address_options.map((option) => (
                            <label key={option} className="pl-4">
                              <input
                                type="radio"
                                name="address"
                                value={option}
                                checked={sheetOption.address === option}
                                onChange={(e) =>
                                  handleOptionChange("address", e.target.value)
                                }
                              />
                              {option}
                            </label>
                          ))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div>
              <div className="flex mb-2">
                <div className="rounded-full mt-1.5 border-slate-100 mr-3 w-5 h-5 title-circle"></div>
                <div className="text-lg text-slate-500 font-black">
                  保有資格
                </div>
              </div>
              <div className=" border-2 border-slate-300 rounded-2xl overflow-auto max-h-[25vh]">
                {isLoading ? (
                  <table className="table-auto text-slate-600 w-full border-collapse">
                    <tbody>
                      {localQualifications.map((qualification, idx) => (
                        <tr key={idx}>
                          <th
                            className={`${
                              idx % 2 === 0 ? "bg-slate-100" : "bg-eaf0f6"
                            } w-28 bg-slate-100 px-4 py-2 text-right border-r-2`}
                          >
                            {"資格 " + (idx + 1)}
                          </th>
                          <td>
                            <div className="w-[95%] mx-auto h-5 bg-slate-200 rounded w-24 animate-pulse"></div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="table-auto text-slate-600 w-full border-collapse">
                    <tbody>
                      {localQualifications.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="text-center py-4 text-slate-400"
                          >
                            保有資格はありません
                          </td>
                        </tr>
                      ) : (
                        localQualifications.map((qualification, idx) => (
                          <tr key={idx}>
                            <th
                              className={`${
                                idx % 2 === 0 ? "bg-slate-100" : "bg-eaf0f6"
                              } w-28 bg-slate-100 px-4 py-2 text-right border-r-2`}
                            >
                              {"資格 " + (idx + 1)}
                            </th>
                            <td
                              className={`${
                                idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } ${
                                qualification.isDisplay ? "" : "text-slate-300"
                              } pl-4 py-2 w-80`}
                            >
                              {qualification.name}
                            </td>
                            <td
                              className={`${
                                idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                              } px-4 py-2 border-r-2`}
                            >
                              <label className="pl-4">
                                <input
                                  type="radio"
                                  name={`qualification-${qualification.id}`}
                                  checked={qualification.isDisplay}
                                  onChange={() =>
                                    handleQCOptionChange(qualification.id, true)
                                  }
                                />
                                表示
                              </label>
                              <label className="pl-4">
                                <input
                                  type="radio"
                                  name={`qualification-${qualification.id}`}
                                  checked={!qualification.isDisplay}
                                  onChange={() =>
                                    handleQCOptionChange(
                                      qualification.id,
                                      false
                                    )
                                  }
                                />
                                非表示
                              </label>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end space-x-5">
            {isLoading ? (
              <>
                <div className="w-28 h-10 mt-2 bg-slate-200 rounded-3xl animate-pulse"></div>
                <div className="w-36 h-10 mt-2 bg-slate-200 rounded-3xl animate-pulse"></div>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="bg-gray-700 text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  キャンセル
                </button>
                <button
                  onClick={createPdf}
                  className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
                >
                  <FontAwesomeIcon icon={faPrint} className="mr-2" />
                  印刷プレビュー
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <ErrorModal
        isOpen={isErrorModalOpen}
        title="エラー"
        text={errorMessage}
        onClose={() => setErrorModalOpen(false)}
      />
    </div>
  );
};

export default PrintPreviewModal;
